import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, LogOut, DollarSign, Clock, CheckCircle2, Download, Megaphone, Gift, Link2, Star, RefreshCw } from 'lucide-react';
import { ambassadorMe, ambassadorCommissions, ambassadorPayouts, ambassadorAssets, ambassadorAnnouncements } from '../api';
import { useToast } from '../hooks/use-toast';

const AmbassadorDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [comms, setComms] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [anns, setAnns] = useState([]);
  const [tab, setTab] = useState('overview');
  const [err, setErr] = useState(false);

  const load = async () => {
    try {
      const m = await ambassadorMe();
      setMe(m);
      setComms(await ambassadorCommissions());
      setPayouts(await ambassadorPayouts());
      setAssets(await ambassadorAssets());
      setAnns(await ambassadorAnnouncements());
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem('pe_amb_token'); navigate('/ambassador/login'); }
      else setErr(true);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('pe_amb_token')) { navigate('/ambassador/login'); return; }
    load();
    // eslint-disable-next-line
  }, []);

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast({ title: 'Copied!', description: txt }); };
  const logout = () => { localStorage.removeItem('pe_amb_token'); navigate('/ambassador/login'); };

  if (err) return <div className="pt-40 pb-40 text-center font-cormorant text-2xl text-[#f5ecd9]/60">Your account is not active. Please contact the Prestige team.</div>;
  if (!me) return <div className="pt-40 pb-40 text-center font-cormorant text-2xl text-[#f5ecd9]/60">Loading your dashboard…</div>;

  const a = me.ambassador;
  const s = me.stats;
  const origin = window.location.origin;
  const refLink = a.referral_link && a.referral_link.startsWith('http') ? a.referral_link : `${origin}/shop?ref=${a.referral_code || ''}`;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'commissions', label: 'Commissions' },
    { id: 'payouts', label: 'Payouts' },
    { id: 'assets', label: 'Brand Assets' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'profile', label: 'Profile' },
  ];

  return (
    <div className="pt-28 pb-24 min-h-screen max-w-6xl mx-auto px-5">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase">Ambassador · {a.rank}</p>
          <h1 className="font-cinzel text-3xl md:text-4xl text-gold-gradient">Welcome, {a.name.split(' ')[0]}</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={load} className="flex items-center gap-1.5 text-xs font-jost text-[#f0d582] border border-[#d4af37]/30 rounded-full px-4 py-2 hover:bg-[#d4af37]/10"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
          <button onClick={logout} className="flex items-center gap-1.5 text-xs font-jost text-[#ff4da6] border border-[#ff4da6]/30 rounded-full px-4 py-2 hover:bg-[#ff4da6]/10"><LogOut className="h-3.5 w-3.5" /> Logout</button>
        </div>
      </div>

      {/* Referral codes */}
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="card-glass rounded-2xl p-6">
          <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/50 flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5 text-[#f0d582]" /> Your Referral Link</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 text-sm text-[#f0d582] font-jost truncate">{refLink}</code>
            <button onClick={() => copy(refLink)} className="text-[#f0d582] hover:text-[#ff4da6]"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="card-glass rounded-2xl p-6">
          <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/50 flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-[#f0d582]" /> Your Discount Code</p>
          <div className="flex items-center gap-2 mt-2">
            <code className="flex-1 text-2xl text-gold-gradient font-cinzel tracking-widest">{a.discount_code || '—'}</code>
            <button onClick={() => copy(a.discount_code || '')} className="text-[#f0d582] hover:text-[#ff4da6]"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-jost transition-all ${tab === t.id ? 'bg-gradient-to-r from-[#d6197d] to-[#ff4da6] text-white' : 'border border-[#d4af37]/30 text-[#f5ecd9]/70 hover:text-[#f0d582]'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: 'Total Sales', v: `$${s.sales.toFixed(2)}`, icon: DollarSign },
            { l: 'Pending Commission', v: `$${s.pending.toFixed(2)}`, icon: Clock },
            { l: 'Paid Commission', v: `$${s.paid.toFixed(2)}`, icon: CheckCircle2 },
            { l: 'Commission Rate', v: `${a.commission_pct}%`, icon: Star },
          ].map((c) => (
            <div key={c.l} className="card-glass rounded-2xl p-6 text-center"><c.icon className="h-6 w-6 text-[#f0d582] mx-auto mb-3" /><div className="font-cinzel text-2xl text-gold-gradient">{c.v}</div><div className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/50 mt-2">{c.l}</div></div>
          ))}
          <div className="col-span-2 md:col-span-4 card-glass rounded-2xl p-6">
            <p className="font-jost text-sm text-[#f5ecd9]/70">Ambassador Kit: <span className="text-[#f0d582] uppercase tracking-widest text-xs">{(a.kit_status || 'not_sent').replace('_', ' ')}</span></p>
            {a.admin_notes && <p className="font-jost text-sm text-[#f5ecd9]/60 mt-2 italic">Note from Prestige: “{a.admin_notes}”</p>}
          </div>
        </div>
      )}

      {tab === 'commissions' && (
        <div className="space-y-3">
          {comms.length === 0 && <p className="font-cormorant text-xl text-[#f5ecd9]/50">No commissions yet. Share your code to start earning!</p>}
          {comms.map((c) => (
            <div key={c.id} className="card-glass rounded-xl p-5 flex items-center justify-between">
              <div><p className="font-jost text-sm text-[#f5ecd9]">Sale ${c.amount_sale.toFixed(2)} · {c.commission_pct}%</p><p className="font-jost text-xs text-[#f5ecd9]/40 mt-1">{new Date(c.created_at).toLocaleDateString()} · {c.note}</p></div>
              <div className="text-right"><p className="font-cinzel text-gold-gradient">${c.commission_amount.toFixed(2)}</p><span className={`text-[10px] uppercase tracking-widest ${c.status === 'paid' ? 'text-[#2ecc71]' : 'text-[#f0d582]'}`}>{c.status}</span></div>
            </div>
          ))}
        </div>
      )}

      {tab === 'payouts' && (
        <div className="space-y-3">
          {payouts.length === 0 && <p className="font-cormorant text-xl text-[#f5ecd9]/50">No payouts yet.</p>}
          {payouts.map((p) => (
            <div key={p.id} className="card-glass rounded-xl p-5 flex items-center justify-between">
              <div><p className="font-jost text-sm text-[#f5ecd9]">{p.method}</p><p className="font-jost text-xs text-[#f5ecd9]/40 mt-1">{new Date(p.created_at).toLocaleDateString()} · {p.note}</p></div>
              <p className="font-cinzel text-gold-gradient">${p.amount.toFixed(2)}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'assets' && (
        <div className="grid md:grid-cols-3 gap-5">
          {assets.length === 0 && <p className="font-cormorant text-xl text-[#f5ecd9]/50">No brand assets uploaded yet.</p>}
          {assets.map((as) => (
            <div key={as.id} className="card-glass rounded-2xl overflow-hidden">
              {as.type === 'caption' || as.type === 'instructions' ? (
                <div className="p-5"><span className="text-[10px] uppercase tracking-widest text-[#ff4da6]">{as.type}</span><h4 className="font-cinzel text-lg text-[#f5ecd9] mt-1">{as.title}</h4><p className="font-jost text-sm text-[#f5ecd9]/70 mt-2 whitespace-pre-wrap">{as.caption || as.instructions}</p></div>
              ) : (
                <>
                  {as.url && <div className="aspect-video bg-[#120d10] overflow-hidden"><img src={as.url} alt={as.title} className="w-full h-full object-cover" /></div>}
                  <div className="p-4">
                    <h4 className="font-cinzel text-[#f5ecd9]">{as.title}</h4>
                    {as.caption && <p className="font-jost text-xs text-[#f5ecd9]/60 mt-1">{as.caption}</p>}
                    {as.url && <a href={as.url} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1.5 mt-3 text-xs text-[#f0d582] hover:text-[#ff4da6] uppercase tracking-widest"><Download className="h-3.5 w-3.5" /> Download</a>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="space-y-4">
          {anns.length === 0 && <p className="font-cormorant text-xl text-[#f5ecd9]/50">No active campaigns right now.</p>}
          {anns.map((an) => (
            <div key={an.id} className="card-glass rounded-2xl p-6"><div className="flex items-center gap-2 mb-2"><Megaphone className="h-4 w-4 text-[#ff4da6]" /><h4 className="font-cinzel text-lg text-[#f0d582]">{an.title}</h4></div><p className="font-jost text-sm text-[#f5ecd9]/70 whitespace-pre-wrap">{an.body}</p></div>
          ))}
        </div>
      )}

      {tab === 'profile' && (
        <div className="card-glass rounded-2xl p-8 max-w-lg space-y-3 font-jost text-sm">
          {[['Name', a.name], ['Email', a.email], ['Phone', a.phone || '—'], ['Social', a.social || '—'], ['Shipping', a.shipping_address || '—'], ['Rank', a.rank], ['Status', a.status]].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-white/5 pb-2"><span className="text-[#f5ecd9]/50">{k}</span><span className="text-[#f5ecd9]">{v}</span></div>
          ))}
          <p className="font-jost text-xs text-[#f5ecd9]/40 pt-2">To update your details, please contact the Prestige team.</p>
        </div>
      )}
    </div>
  );
};

export default AmbassadorDashboard;
